import React, { useState, useEffect } from 'react';
import { UserContext } from './userContextDefinition';
import type { UserContextType, AppUser } from './userContextDefinition';
import type { Notification as NotificationType } from '../types/Notification';
import type { Badge } from '../types/Badge';
import type { BadgeActivity } from '../types/Activity';
import type { HomeBrewReview } from '../types/HomeBrewReview';

/**
 * Check IndexedDB for Firebase Auth tokens WITHOUT loading Firebase.
 * Uses indexedDB.databases() to check existence before opening, so no
 * database is created on first visit (avoids Lighthouse IndexedDB warning).
 */
async function checkIndexedDBForAuth(): Promise<boolean> {
  try {
    const dbName = 'firebaseLocalStorageDb';

    // Fast path: use indexedDB.databases() if available (all modern browsers)
    // This checks existence without creating the DB.
    if (typeof indexedDB.databases === 'function') {
      const dbs = await indexedDB.databases();
      if (!dbs.some((d) => d.name === dbName)) return false;
    }

    return new Promise((resolve) => {
      const request = indexedDB.open(dbName);

      request.onsuccess = () => {
        const db = request.result;
        if (db.objectStoreNames.length === 0) {
          db.close();
          resolve(false);
          return;
        }
        try {
          const transaction = db.transaction(['firebaseLocalStorage'], 'readonly');
          const store = transaction.objectStore('firebaseLocalStorage');
          const getAllRequest = store.getAll();
          getAllRequest.onsuccess = () => {
            const records = getAllRequest.result;
            db.close();
            resolve(records && records.length > 0);
          };
          getAllRequest.onerror = () => { db.close(); resolve(false); };
        } catch {
          db.close();
          resolve(false);
        }
      };

      request.onerror = () => resolve(false);
      // Only fires if DB didn't exist — close immediately without creating stores
      request.onupgradeneeded = () => { request.result.close(); resolve(false); };
    });
  } catch {
    return false;
  }
}

/**
 * Lightweight UserProvider that provides guest state by default
 * and lazy-loads the full UserProvider only when authentication is detected.
 * 
 * This prevents Firebase from loading on initial page load for unauthenticated users.
 */
export const GuestUserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authResolved, setAuthResolved] = useState(false);
  const [FullUserProvider, setFullUserProvider] = useState<React.ComponentType<{ children: React.ReactNode }> | null>(null);

  useEffect(() => {
    // Check for logout flag first (synchronous check)
    const isLoggingOut = localStorage.getItem('isLoggingOut');
    if (isLoggingOut === 'true') {
      setIsAuthenticated(false);
      setAuthResolved(true);
      return;
    }

    let unsubscribe: (() => void) | undefined;

    const initAuth = async () => {
      try {
        // Check IndexedDB for Firebase Auth tokens WITHOUT loading Firebase
        const hasAuthTokens = await checkIndexedDBForAuth();
        
        if (!hasAuthTokens) {
          setIsAuthenticated(false);
          setAuthResolved(true);
          return;
        }

        // Only load Firebase if we found auth tokens
        const { getFirebaseAuth, getOnAuthStateChanged } = await import('../lib/firebaseLazy');
        const [auth, onAuthStateChanged] = await Promise.all([
          getFirebaseAuth(),
          getOnAuthStateChanged(),
        ]);
        
        // Subscribe to auth state changes
        unsubscribe = onAuthStateChanged(auth, (user) => {
          setIsAuthenticated(!!user);
          setAuthResolved(true);
        });
      } catch (error) {
        console.error('[GuestUserProvider] Error initializing auth:', error);
        setIsAuthenticated(false);
        setAuthResolved(true);
      }
    };

    initAuth();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  useEffect(() => {
    // Lazy-load full UserProvider when authenticated
    if (isAuthenticated && !FullUserProvider) {
      import('./UserContext').then((module) => {
        setFullUserProvider(() => module.UserProvider);
      });
    }
  }, [isAuthenticated, FullUserProvider]);

  // If authenticated and UserProvider is loaded, use it
  if (isAuthenticated && FullUserProvider) {
    return <FullUserProvider>{children}</FullUserProvider>;
  }

  // Otherwise, provide guest state
  const guestContextValue: UserContextType = {
    user: null,
    loading: !authResolved || (isAuthenticated && !FullUserProvider), // Keep loading true until auth is resolved AND UserProvider is loaded
    setUser: () => {},
    setLoading: () => {},
    setNotifications: () => {},
    login: () => {},
    logout: () => {},
    badges: [],
    addBadge: () => {},
    activities: [],
    addActivity: () => {},
    notifications: [],
    addNotification: () => {},
    markAllNotificationsRead: () => {},
    deleteAllNotifications: () => {},
    updatePhoto: () => {},
    startNotificationsListener: () => {},
    stopNotificationsListener: () => {},
    pushSubscription: null,
    notificationsEnabled: false,
    setNotificationsEnabled: () => {},
    enableNotifications: async () => {},
    disableNotifications: async () => {},
    updateFlavorProfile: () => {},
    showBadgeDialog: () => {},
  };

  return (
    <UserContext.Provider value={guestContextValue}>
      {children}
    </UserContext.Provider>
  );
};
